import { useAuthStore } from '@/stores/auth.store'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

export interface NotificationPreference {
  childId: string
  pushEnabled: boolean
  quietHoursStart: string | null
  quietHoursEnd: string | null
}

export interface PushToken {
  id: string
  platform: 'ios' | 'android' | 'web'
  deviceId?: string
  createdAt: string
}

export const notificationsApi = {
  /**
   * Register a push token for the current device
   */
  async registerToken(
    token: string,
    platform: 'ios' | 'android' | 'web',
    deviceId?: string
  ): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ token, platform, deviceId }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Registration failed' }))
      throw new Error(error.error || 'Failed to register push token')
    }
  },

  /**
   * Unregister a push token (on logout)
   */
  async unregisterToken(token: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/unregister`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ token }),
    })

    if (!response.ok) {
      // Don't throw on unregister errors - user is logging out anyway
      console.warn('Failed to unregister push token')
    }
  },

  /**
   * Get notification preferences for all children (parents only)
   */
  async getPreferences(): Promise<NotificationPreference[]> {
    const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load preferences' }))
      throw new Error(error.error || 'Failed to load notification preferences')
    }

    const data = await response.json()
    return data.preferences
  },

  /**
   * Update notification preferences for a specific child (parents only)
   */
  async updatePreferences(
    childId: string,
    preferences: {
      pushEnabled?: boolean
      quietHoursStart?: string | null
      quietHoursEnd?: string | null
    }
  ): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ childId, ...preferences }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Update failed' }))
      throw new Error(error.error || 'Failed to update notification preferences')
    }
  },

  /**
   * Get all registered tokens for current user (debugging)
   */
  async getTokens(): Promise<PushToken[]> {
    const response = await fetch(`${API_BASE_URL}/notifications/tokens`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })

    if (!response.ok) {
      throw new Error('Failed to load tokens')
    }

    const data = await response.json()
    return data.tokens
  },
}
